import { useContext, useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  Table,
  Loader,
  SvgIcon,
  IColumn,
  getDuration,
  ICheckedData,
  LabelWithIcon,
  IDashboardGroupDetailsCount,
} from '@ascd/witsby-components';
import { Box, Grid, Tooltip, IconButton } from '@mui/material';
import * as FileSaver from 'file-saver';
import { debounce, get, isEmpty } from 'lodash';
import * as XLSX from 'xlsx';
import { ASCD_ID } from '@constants';
import { AppContext } from '@contexts/appContext';
import { DashboardContext, eDashboardActionType } from '@contexts/dashboardContext';
import DASHBOARD_USER_COUNT_DETAILS from '@graphql/schema/dashboardUserDetailsCount.graphql';
import { ITableFilter } from '@types';
import {
  handleGraphqlError,
  DASHBOARD_FILTER_TYPES,
  DASHBOARD_FILTER_DEFAULT_CHECKED_DATA,
  showToast,
  dateFormat,
  scrollToTop,
} from '@utils';

const AdminDashboardLearnings = () => {
  const {
    state: { currentInstitution },
  } = useContext(AppContext);

  const {
    dispatch,
    state: { sideBarApiFilter },
  } = useContext(DashboardContext);

  const {
    assignedBy,
    internalId1,
    selectedInstitution,
    createdByOrganizationId,
    selectedGroup,
    dates: { startDate, endDate },
  } = sideBarApiFilter;

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [filter, setFilter] = useState<ITableFilter>({
    page: 0,
    limit: 20,
    searchText: '',
    searchField: ['userInfo.name'],
    sortDirection: 'DESC',
    sortField: 'clockHours',
  });

  // Handle institution selection filter for ASCD admins
  const currentSelectedInstitution = useMemo(() => {
    if (!isEmpty(selectedInstitution)) return selectedInstitution;

    return {
      value: currentInstitution._id,
      label: currentInstitution.name,
    };
  }, [currentInstitution._id, currentInstitution.name, selectedInstitution]);

  const variables = {
    filter,
    endDate,
    startDate,
    assignedBy,
    titles: null,
    managerIds: null,
    organizationId:
      currentSelectedInstitution?.label === 'ALL' ? '' : currentSelectedInstitution.value,
    createdByOrganizationId: isEmpty(createdByOrganizationId)
      ? [currentSelectedInstitution.value, ASCD_ID]
      : createdByOrganizationId,
    schoolIds:
      selectedGroup.length > 0 ||
      currentSelectedInstitution?.label === 'ASCD' ||
      currentSelectedInstitution?.label === 'ALL'
        ? null
        : (internalId1 || []).map((internalId) => internalId?.internalId),
    groupIds: (selectedGroup || []).map((val) => val._id),
  };

  const { loading, data } = useQuery(DASHBOARD_USER_COUNT_DETAILS, {
    variables,
    fetchPolicy: 'network-only',
  });

  const [getCSVDetails, { loading: isCSVLoading }] = useLazyQuery(DASHBOARD_USER_COUNT_DETAILS, {
    fetchPolicy: 'network-only',
  });

  const dashBoardUserDetailsCount = get(data, 'dashBoardUserDetailsCount', {
    totalCount: 0,
    data: [],
  });

  const exportToCSV = async () => {
    if (!isCSVLoading) {
      getCSVDetails({
        variables: {
          ...variables,
          filter: {
            ...filter,
            page: 0,
            limit: dashBoardUserDetailsCount.totalCount,
          },
        },
      })
        .then((res) => {
          const datalist = get(res, 'data.dashBoardUserDetailsCount.data', [])
            .filter((filterItem: IDashboardGroupDetailsCount) =>
              selectedRows.length > 0 ? selectedRows.includes(filterItem.userId) : true,
            )
            .map((item: IDashboardGroupDetailsCount) => ({
              'Learner Name': item.name,
              'School Name': item.schoolName,
              'Clock Hours': `${getDuration(item.clockHours, true, true, true)}hrs`,
              'Learning Items': item.items,
              Courses: item.courses,
              'Learning Lists': item.lists,
              Strategies: item.strategies,
              'El Magazines': item.elMagazine,
              'E Books': item.ebooks,
              Webinars: item.webinar,
              // 'Last Login': dateFormat(item.lastAccess, 'dd-MMM-yyyy'),
            }));
          const fileType =
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          const fileExtension = '.xlsx';
          const ws = XLSX.utils.json_to_sheet(datalist);
          const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
          const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const csvData = new Blob([excelBuffer], { type: fileType });
          FileSaver.saveAs(
            csvData,
            selectedRows.length > 1 || selectedRows.length === 0
              ? `Learnings (${dateFormat({ date: startDate, format: 'dd-MMM-yyyy' })} - ${dateFormat(
                  { date: endDate, format: 'dd-MMM-yyyy' },
                )}) ${fileExtension}`
              : `${datalist[0]['Learner Name']} (${dateFormat({
                  date: startDate,
                  format: 'dd-MMM-yyyy',
                })} - ${dateFormat({ date: endDate, format: 'dd-MMM-yyyy' })}) ${fileExtension}`,
          );
          showToast('Downloaded Successfully');
        })
        .catch(handleGraphqlError);
    }
  };

  const handleLearningTable = (
    learningsData: IDashboardGroupDetailsCount[],
    status: ICheckedData[],
    type: ICheckedData[],
  ) => {
    scrollToTop();
    dispatch({
      type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
      data: {
        type: 'LEARNINGS',
        detailsPageData: { learningsData, status, type },
      },
    });
  };

  const learningColumn: IColumn[] = [
    {
      key: 'name',
      name: 'Learner Name',
      render: (_, value) => (
        <Box
          tabIndex={0}
          sx={{
            cursor: 'pointer',
            '&:hover,&:focus': {
              outline: 'none',
              textDecoration: 'underline',
              color: (theme) => theme.palette.primary.light,
            },
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleLearningTable(
                [value as IDashboardGroupDetailsCount],
                DASHBOARD_FILTER_DEFAULT_CHECKED_DATA,
                DASHBOARD_FILTER_TYPES,
              );
            }
          }}
          onClick={() => {
            handleLearningTable(
              [value as IDashboardGroupDetailsCount],
              DASHBOARD_FILTER_DEFAULT_CHECKED_DATA,
              DASHBOARD_FILTER_TYPES,
            );
          }}>
          {(value as IDashboardGroupDetailsCount).name}
        </Box>
      ),
    },
    {
      key: 'schoolName',
      name: 'School Name',
      render: (_, value) => (
        <Box sx={{ minWidth: 150 }}>{(value as IDashboardGroupDetailsCount).schoolName}</Box>
      ),
      isSorting: true,
      ...(filter.sortField === 'schoolName' && {
        sortType: filter.sortDirection,
      }),
    },
    {
      align: 'center',
      isSorting: true,
      key: 'clockHours',
      name: 'Clock Hours',
      headerAlign: 'center',
      ...(filter.sortField === 'clockHours' && {
        sortType: filter.sortDirection,
      }),
      render: (_, value) => (
        <Box>
          {getDuration((value as IDashboardGroupDetailsCount).clockHours, true, true, true)}
          hrs
        </Box>
      ),
    },
    {
      width: '10%',
      key: 'items',
      name: 'Learning Items',
      align: 'center',
      isSorting: true,
      headerAlign: 'center',
      ...(filter.sortField === 'items' && {
        sortType: filter.sortDirection,
      }),
      render: (_, value) => (
        <Box
          onClick={() =>
            handleLearningTable(
              [value as IDashboardGroupDetailsCount],
              [{ _id: 'COMPLETED', name: 'Completed' }],
              DASHBOARD_FILTER_TYPES.filter((item) => item._id === 'LEARNING_ITEMS'),
            )
          }
          sx={{
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: (theme) => theme.palette.primary.light,
            },
          }}>
          {(value as IDashboardGroupDetailsCount).items}
        </Box>
      ),
    },
    {
      width: '10%',
      key: 'courses',
      name: 'Courses',
      align: 'center',
      isSorting: true,
      headerAlign: 'center',
      render: (_, value) => (
        <Box
          sx={{
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: (theme) => theme.palette.primary.light,
            },
          }}
          onClick={() => {
            handleLearningTable(
              [value as IDashboardGroupDetailsCount],
              [{ _id: 'COMPLETED', name: 'Completed' }],
              DASHBOARD_FILTER_TYPES.filter((item) => item._id === 'COURSES'),
            );
          }}>
          {(value as IDashboardGroupDetailsCount).courses}
        </Box>
      ),

      ...(filter.sortField === 'courses' && {
        sortType: filter.sortDirection,
      }),
    },
    {
      key: 'lists',
      name: 'Learning Lists',
      align: 'center',
      isSorting: true,
      headerAlign: 'center',
      ...(filter.sortField === 'lists' && {
        sortType: filter.sortDirection,
      }),
      render: (_, value) => (
        <Box
          onClick={() =>
            handleLearningTable(
              [value as IDashboardGroupDetailsCount],
              [{ _id: 'COMPLETED', name: 'Completed' }],
              DASHBOARD_FILTER_TYPES.filter((item) => item._id === 'LEARNING_LISTS'),
            )
          }
          sx={{
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: (theme) => theme.palette.primary.light,
            },
          }}>
          {(value as IDashboardGroupDetailsCount).lists}
        </Box>
      ),
    },
    {
      key: 'strategies',
      name: 'Strategies',
      align: 'center',
      headerAlign: 'center',
      width: '10%',
      isSorting: true,
      ...(filter.sortField === 'strategies' && {
        sortType: filter.sortDirection,
      }),
      render: (_, value) => (
        <Box
          onClick={() =>
            handleLearningTable(
              [value as IDashboardGroupDetailsCount],
              [{ _id: 'COMPLETED', name: 'Completed' }],
              DASHBOARD_FILTER_TYPES.filter((item) => item._id === 'STRATEGY'),
            )
          }
          sx={{
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: (theme) => theme.palette.primary.light,
            },
          }}>
          {(value as IDashboardGroupDetailsCount).strategies}
        </Box>
      ),
    },

    {
      align: 'center',
      isSorting: true,
      key: 'elMagazine',
      name: 'EL Magazines',
      headerAlign: 'center',
      ...(filter.sortField === 'elMagazine' && {
        sortType: filter.sortDirection,
      }),
      render: (_, value) => (
        <Box
          onClick={() =>
            handleLearningTable(
              [value as IDashboardGroupDetailsCount],
              [{ _id: 'COMPLETED', name: 'Completed' }],
              DASHBOARD_FILTER_TYPES.filter((item) => item._id === 'EL_MAGAZINE'),
            )
          }
          sx={{
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: (theme) => theme.palette.primary.light,
            },
          }}>
          {(value as IDashboardGroupDetailsCount).elMagazine}
        </Box>
      ),
    },
    {
      key: 'ebooks',
      name: 'EBooks',
      align: 'center',
      isSorting: true,
      headerAlign: 'center',
      ...(filter.sortField === 'ebooks' && {
        sortType: filter.sortDirection,
      }),
      render: (_, value) => (
        <Box
          onClick={() =>
            handleLearningTable(
              [value as IDashboardGroupDetailsCount],
              [{ _id: 'COMPLETED', name: 'Completed' }],
              DASHBOARD_FILTER_TYPES.filter((item) => item._id === 'EBOOK'),
            )
          }
          sx={{
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: (theme) => theme.palette.primary.light,
            },
          }}>
          {(value as IDashboardGroupDetailsCount).ebooks}
        </Box>
      ),
    },
    {
      key: 'webinar',
      align: 'center',
      isSorting: true,
      name: 'Webinars',
      headerAlign: 'center',
      ...(filter.sortField === 'webinar' && {
        sortType: filter.sortDirection,
      }),
      render: (_, value) => (
        <Box
          onClick={() =>
            handleLearningTable(
              [value as IDashboardGroupDetailsCount],
              [{ _id: 'COMPLETED', name: 'Completed' }],
              DASHBOARD_FILTER_TYPES.filter((item) => item._id === 'WEBINAR'),
            )
          }
          sx={{
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: (theme) => theme.palette.primary.light,
            },
          }}>
          {(value as IDashboardGroupDetailsCount).webinar}
        </Box>
      ),
    },
    // {
    //   isSorting: true,
    //   key: 'lastAccess',
    //   name: 'Last Login',
    //   ...(filter.sortField === 'lastAccess' && {
    //     sortType: filter.sortDirection,
    //   }),
    //   render: (_, value) => {
    //     if ((value as IDashboardGroupDetailsCount)?.lastAccess) {
    //       return (
    //         <Box>{dateFormat((value as IDashboardGroupDetailsCount).lastAccess, 'mm/dd/yyyy')}</Box>
    //       );
    //     }
    //     return <Box sx={{ pl: 5 }}>-</Box>;
    //   },
    // },
  ];

  return (
    <Grid item xl={12} sm={12} xs={12}>
      <Table
        isSearchEnable
        loading={loading}
        page={filter.page}
        apiPaginationEnable
        pageSize={filter.limit}
        columns={learningColumn}
        searchValue={filter.searchText}
        searchPlaceHolder="Search Learner"
        activeSortField={filter.sortField}
        data={dashBoardUserDetailsCount.data}
        count={dashBoardUserDetailsCount.totalCount}
        dataEmptyChildren={<Box>No learning found</Box>}
        isSelection
        selectionField="userId"
        selectedRows={selectedRows}
        onHandleSelection={(selectedRow) => setSelectedRows(selectedRow)}
        handlePageChange={(event, newPage) => {
          setFilter({ ...filter, page: newPage });
        }}
        handleRowsPerPageChange={(e) => {
          setFilter({ ...filter, page: 0, limit: parseInt(e.target.value, 10) });
        }}
        onHandleSearch={debounce((e) => {
          setFilter({ ...filter, page: 0, searchText: e.target.value });
        }, 500)}
        onSortClick={(key, sortType) => {
          setFilter({
            ...filter,
            page: 0,
            sortDirection: sortType,
            sortField: key as string,
          });
        }}
        headerChildren={
          <Box>
            <LabelWithIcon
              title="Learnings"
              iconDirection="left"
              iconColorHex="#FFFFFF"
              iconName="LEARNING_LIST"
              sx={{ color: '#FFFFFF' }}
            />
          </Box>
        }
        headerExtra={
          dashBoardUserDetailsCount.totalCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}>
              <Tooltip describeChild title="View portfolio report" arrow>
                <IconButton
                  sx={{
                    ml: (theme) => theme.spaces.REGULAR,
                    cursor: selectedRows.length ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={() => {
                    if (selectedRows.length) {
                      handleLearningTable(
                        dashBoardUserDetailsCount.data?.filter(
                          (item: IDashboardGroupDetailsCount) => selectedRows.includes(item.userId),
                        ),
                        DASHBOARD_FILTER_DEFAULT_CHECKED_DATA,
                        DASHBOARD_FILTER_TYPES,
                      );
                    }
                  }}
                  aria-label="Handle report">
                  <SvgIcon icon="CLICK_ICON" sx={{ fill: '#FFFF' }} />
                </IconButton>
              </Tooltip>
              {isCSVLoading ? (
                <Loader size={20} sx={{ color: '#FFFFFF', ml: (theme) => theme.spaces.LARGE }} />
              ) : (
                <Tooltip describeChild title="Download the report below" arrow>
                  <IconButton onClick={() => exportToCSV()} aria-label="Handle course">
                    <SvgIcon icon="FILE_DOWNLOAD" viewBox="0 0 22 29" sx={{ fill: '#FFFF' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )
        }
      />
    </Grid>
  );
};

export default AdminDashboardLearnings;
